import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setting } from "../services";

// Initial state
const initialState = {
	companies: [],
	selectedCompany: null,
	loading: {
		companies: false,
		create: false,
		update: false,
		delete: false,
	},
	error: {
		companies: null,
		create: null,
		update: null,
		delete: null,
	},
};

// Async thunks
export const fetchCompanies = createAsyncThunk(
	"companies/fetchAll",
	async (_, { rejectWithValue }) => {
		try {
			const response = await setting.getCompanies();
			return response?.items || [];
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to fetch companies",
			);
		}
	},
);

export const createCompany = createAsyncThunk(
	"companies/createCompany",
	async (companyData, { rejectWithValue }) => {
		try {
			const response = await setting.addCompany(companyData);
			return response;
		} catch (error) {
			return rejectWithValue(error.message || "Failed to create company");
		}
	},
);

export const updateCompany = createAsyncThunk(
	"companies/updateCompany",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const response = await setting.updateCompany(id, data);
			return { ...response, id };
		} catch (error) {
			return rejectWithValue(error.message || "Failed to update company");
		}
	},
);

export const deleteCompany = createAsyncThunk(
	"companies/deleteCompany",
	async (companyId, { rejectWithValue }) => {
		try {
			await setting.deleteCompany(companyId);
			return companyId;
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to delete company";
			const data =
				error.response?.data?.data ?? error.data ?? null;
			return rejectWithValue(data ? { message, data } : message);
		}
	},
);

// Company slice
const companySlice = createSlice({
	name: "companies",
	initialState,
	reducers: {
		setSelectedCompany: (state, action) => {
			state.selectedCompany = action.payload;
		},
		clearSelectedCompany: (state) => {
			state.selectedCompany = null;
		},
		clearErrors: (state) => {
			state.error = {
				companies: null,
				create: null,
				update: null,
				delete: null,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch companies
			.addCase(fetchCompanies.pending, (state) => {
				state.loading.companies = true;
				state.error.companies = null;
			})
			.addCase(fetchCompanies.fulfilled, (state, action) => {
				state.loading.companies = false;
				state.companies = action.payload;
			})
			.addCase(fetchCompanies.rejected, (state, action) => {
				state.loading.companies = false;
				state.error.companies = action.payload;
			})

			// Create company
			.addCase(createCompany.pending, (state) => {
				state.loading.create = true;
				state.error.create = null;
			})
			.addCase(createCompany.fulfilled, (state, action) => {
				state.loading.create = false;
				// Note: The list is refreshed in the component after creation
				// No need to push here since backend returns only { message }
			})
			.addCase(createCompany.rejected, (state, action) => {
				state.loading.create = false;
				state.error.create = action.payload;
			})

			// Update company
			.addCase(updateCompany.pending, (state) => {
				state.loading.update = true;
				state.error.update = null;
			})
			.addCase(updateCompany.fulfilled, (state, action) => {
				state.loading.update = false;
				const index = state.companies.findIndex(
					(company) => company.id === action.payload.id,
				);
				if (index !== -1) {
					state.companies[index] = action.payload;
				}
			})
			.addCase(updateCompany.rejected, (state, action) => {
				state.loading.update = false;
				state.error.update = action.payload;
			})

			// Delete company
			.addCase(deleteCompany.pending, (state) => {
				state.loading.delete = true;
				state.error.delete = null;
			})
			.addCase(deleteCompany.fulfilled, (state, action) => {
				state.loading.delete = false;
				state.companies = state.companies.filter(
					(company) => company.id !== action.payload,
				);
			})
			.addCase(deleteCompany.rejected, (state, action) => {
				state.loading.delete = false;
				state.error.delete = action.payload;
			});
	},
});

export const { setSelectedCompany, clearSelectedCompany, clearErrors } =
	companySlice.actions;

export default companySlice.reducer;
